import { FormGroup } from "@blueprintjs/core";
import React, { ChangeEvent, useState, useEffect } from "react";
import { MaskedTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import {
  BaseFieldProps,
  FieldLabeledProps,
  MaskedFieldValue,
} from "../../types/fieldTypes";
import { RelativityTooltip } from "../../../Tooltip/RelativityTooltip";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { useMaskedField } from "./useMaskedField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

/** Base MaskedField Props*/
export interface MaskedFieldProps extends BaseFieldProps<MaskedFieldValue> {
  mask?: string;
  promptChar?: string;
  labelProps?: FieldLabeledProps;
  disableToolTip?: boolean;
  cursorPositionOnFocus?: "start" | "end";
  isSensitive?: boolean;
  defaultToSensitiveHidden?: boolean;
  alwaysAllowSensitiveToggle?: boolean;
  sensitiveHiddenFormatter?: (value: string | undefined) => string | undefined;
}

const MaskedField: React.FC<MaskedFieldProps> = (props) => {
  const { localValue, setLocalValue, maskedInputRef, isTooltipVisible } =
    useMaskedField(props);

  const [isSensitiveHidden, setIsSensitiveHidden] = useState<boolean>(true);

  // Initialize sensitive state
  useEffect(() => {
    if (props.isSensitive) {
      setIsSensitiveHidden(props.defaultToSensitiveHidden !== false);
    }
  }, [props.isSensitive, props.defaultToSensitiveHidden, props.disabled]);

  const handleToggleSensitive = () => {
    setIsSensitiveHidden(!isSensitiveHidden);
  };

  const shouldShowSensitiveIcon = () => {
    return (
      props.isSensitive && (!props.disabled || props.alwaysAllowSensitiveToggle)
    );
  };

  return (
    <div className="ucl-base-input-masked-field-container">
      <RelativityTooltip
        shouldShowTooltip={isTooltipVisible && props.disableToolTip !== true}
        content={!!localValue ? localValue.toString() : ""}
      >
        <FormGroup
          className={`${
            props.isRequired && !localValue
              ? "has-required-background"
              : "base-field"
          }`}
        >
          {props.label && (
            <FieldLabel
              labelName={props.label}
              tooltipDescription={props.description}
              fieldProps={{ ...props, value: localValue }}
              isRequired={props.isRequired}
              {...props.labelProps}
            />
          )}
          <div
            className={`masked-field-input-container${
              props.isSensitive && isSensitiveHidden && !!localValue
                ? " sensitive-active"
                : ""
            }`}
          >
            <MaskedTextBoxComponent
              ref={maskedInputRef}
              type={props.type}
              name={props.fieldName}
              value={localValue}
              readonly={!!props.disabled}
              enablePersistence={false}
              mask={props.mask}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (e.target.value != localValue) {
                  setLocalValue(e.target.value);
                }
              }}
              placeholder={props.placeholder}
              promptChar={props.promptChar ? props.promptChar : "_"}
              focus={(args) => {
                if (props.cursorPositionOnFocus === "start") {
                  args.selectionEnd = args.selectionStart = 0;
                } else if (props.cursorPositionOnFocus === "end") {
                  args.selectionStart = args.selectionEnd =
                    args.maskedValue.length;
                }
              }}
            />
            {props.isSensitive && isSensitiveHidden && !!localValue && (
              <div className="masked-field-overlay" aria-hidden="true">
                {props.sensitiveHiddenFormatter
                  ? props.sensitiveHiddenFormatter(localValue)
                  : localValue.replace(/\d/g, "•")}
              </div>
            )}

            {shouldShowSensitiveIcon() && (
              <div
                className="masked-field-icon-container"
                onClick={handleToggleSensitive}
                title={isSensitiveHidden ? "Show value" : "Hide value"}
                aria-label={
                  isSensitiveHidden
                    ? "Click to show value"
                    : "Click to hide value"
                }
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggleSensitive();
                  }
                }}
              >
                <FontAwesomeIcon
                  className="show-password-icon"
                  icon={isSensitiveHidden ? faEye : faEyeSlash}
                  size="lg"
                />
              </div>
            )}
          </div>
          {props.errorMessages && (
            <FormFieldErrorContainer errorMessages={props.errorMessages} />
          )}
        </FormGroup>
      </RelativityTooltip>
    </div>
  );
};

export default MaskedField;
